/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ApolloError } from '@apollo/client';
import {
  VersionControllerClient,
  WorkflowVersion
} from '@foxden/version-controller-client';

import {
  GetQuotePageRedirectQuery,
  useGetQuotePageRedirectQuery
} from '../../generated/graphql';
import { GetQuoteExpired } from '../../generated/rating-quoting-graphql';
import getEnv from '../../utils/getEnv';
import {
  getVersionedCompleteUrl,
  getVersionedTermsUrl
} from '../../utils/getVersionedUrlFromPaymentFrontend';
import { safeAssignLocation } from '../../utils/url';

interface UseQuotePageRedirectReturn {
  loading: boolean;
  error: ApolloError | undefined;
  data: GetQuotePageRedirectQuery | undefined;
}

/**
 * Hook to fetch redirect data for the quote page
 */
export function useQuotePageRedirectQuery(
  applicationId: string
): UseQuotePageRedirectReturn {
  const { loading, error, data } = useGetQuotePageRedirectQuery({
    variables: { applicationId }
  });

  return { loading, error, data };
}

/**
 * Determines if a redirect should occur based on quote page redirect data
 * and performs the redirect if necessary
 */
/* Move from quote page, TODO: clean up logic if needed */
export async function handleQuotePageRedirect(
  redirectData: GetQuotePageRedirectQuery | undefined,
  quoteId: string | undefined,
  isAuthenticated: boolean,
  returnToQuoteExpired: GetQuoteExpired | undefined,
  applicationId: string,
  applicationGroupId: string,
  versionData: WorkflowVersion
): Promise<boolean> {
  if (!redirectData?.getQuotePageRedirect) {
    return false;
  }

  const { REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL } = getEnv();
  const vcc = new VersionControllerClient(
    REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL
  );

  const {
    ownerData,
    policyData,
    invoice,
    applicationData: { transactionType }
  } = redirectData.getQuotePageRedirect;

  // The terms URL is keyed on applicationGroupId and uses the already-resolved
  // versionData, so no per-quote version lookup is needed.
  const proceedToTnCPage = () => {
    safeAssignLocation(getVersionedTermsUrl(applicationGroupId, versionData));
  };

  const proceedToPaymentCompletePage = async (invoiceId: string) => {
    const completeUrl = await getVersionedCompleteUrl(
      applicationId,
      invoiceId,
      vcc
    );
    safeAssignLocation(completeUrl);
  };

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  if (transactionType! === 'New Business') {
    const isTnCSigned = ownerData?.isDocuSignEnvelopeSigned ?? false;
    const isTncSent = ownerData?.isTnCLinkSent ?? false;
    const isPaid = !!policyData;

    if (isTnCSigned && isPaid) {
      // redirect to thank you page if signed & paid
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const {
        invoice: { _id: invoiceId }
      } = redirectData.getQuotePageRedirect.policyData!;
      await proceedToPaymentCompletePage(invoiceId);
      return true;
    } else if (isTnCSigned && !isPaid) {
      // if the quote is expired, show quote expire screen
      if (returnToQuoteExpired) {
        // change this order comparing to previous option
        return false;
      } else if (quoteId) {
        proceedToTnCPage();
        return true;
      } else {
        return false; // wait for quoteId from Quote component
      }
    } else if (!isTnCSigned && isPaid) {
      if (returnToQuoteExpired && returnToQuoteExpired.isGracePeriodExpired) {
        return false; // if grace period exceeds, show quote expire screen
      } else {
        if (isAuthenticated) {
          // redirect to thank you page if unsigned & paid & brokers
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const {
            invoice: { _id: invoiceId }
          } = redirectData.getQuotePageRedirect.policyData!;
          await proceedToPaymentCompletePage(invoiceId);
          return true;
        } else {
          // redirect to T&C page if unsigned & paid & clients
          if (quoteId) {
            proceedToTnCPage();
            return true;
          } else if (returnToQuoteExpired) {
            proceedToTnCPage();
            return true;
          } else {
            return false; // wait for quoteId
          }
        }
      }
    } else if (!isTnCSigned && !isPaid && isTncSent) {
      if (!returnToQuoteExpired) {
        if (!isAuthenticated) {
          // redirect to T&C page if unsigned & unpaid & clients
          if (quoteId) {
            proceedToTnCPage();
            return true;
          } else {
            return false; // wait for quoteId
          }
        }
        // If authenticated (broker), don't redirect - let Quote component handle it
        return false;
      } else {
        return false; // if the quote is expired, show quote expire screen
      }
    }
  } else if (transactionType! === 'Renewal') {
    if (invoice) {
      proceedToTnCPage();
      return true;
    }
  }

  return false;
}

/**
 * Determines if user should be redirected to Client & Agreement page
 */
export function shouldNavigateToClientAgreement(
  redirectData: GetQuotePageRedirectQuery | undefined,
  isAuthenticated: boolean,
  returnToQuoteExpired: GetQuoteExpired | undefined
): boolean {
  if (!redirectData?.getQuotePageRedirect) {
    return false;
  }

  const {
    ownerData,
    policyData,
    applicationData: { transactionType }
  } = redirectData.getQuotePageRedirect;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  if (transactionType! === 'New Business') {
    const isTnCSigned = ownerData?.isDocuSignEnvelopeSigned ?? false;
    const isTncSent = ownerData?.isTnCLinkSent ?? false;
    const isPaid = !!policyData;

    if (
      !isTnCSigned &&
      !isPaid &&
      isTncSent &&
      !returnToQuoteExpired &&
      isAuthenticated
    ) {
      return true;
    }
  }

  return false;
}
